'use client';
import ChevronIcon from '@repo/ui/src/components/Icons/ChevronIcon';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import { useState } from 'react';

export interface NavListProps {
  navItems: Array<{ headerText?: string; title?: string }>;
  visibleSection: number;
  setVisibleSection: (section: number) => void;
}

const NavList = ({ navItems, visibleSection, setVisibleSection }: NavListProps) => {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const labels = navItems?.map((item) => item.headerText ?? item.title);

  return (
    <div>
      <div className="hidden md:block mb-10 sticky top-20 bg-white border border-warm-400 mt-40 max-h-[calc(100vh-6rem)] overflow-auto">
        <nav className="grid px-8 py-10 gap-6">
          <h5 className="tra-text-web-heading-5">
            {t('NavListContent.sectionText', undefined, { default: 'Sections' })}
          </h5>
          <ul className="px-0 grid gap-5 list-none">
            {labels.map((label, index) => {
              // we can't filter these out ahead of time because it screws up the indexing
              if (!label) {
                return null;
              }
              return (
                <li key={label}>
                  <Anchor
                    onClick={(e) => {
                      e.preventDefault();
                      const el = document.getElementById(`${index}header`);
                      if (el) {
                        const above = window.scrollY < el.offsetTop;
                        window.scrollTo({
                          top: el.offsetTop + (above ? 1 : -1),
                          behavior: 'smooth',
                        });
                      }
                    }}
                    link={`#${index}header`}
                    className={classNames(
                      'tra-text-web-body-l hover:text-brand-400',
                      visibleSection === index && 'font-bold',
                    )}
                  >
                    {label}
                  </Anchor>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      <div className="md:hidden relative">
        <button
          className="py-4 px-10 flex justify-between items-center bg-white w-full rounded border-2 border-neutral-800"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
        >
          <h5 className="tra-text-web-utl-button-l">
            {t('NavListContent.sectionText', undefined, { default: 'Sections' })}
          </h5>
          <span className="w-7 inline-block">
            <ChevronIcon direction={open ? 'up' : 'down'} />
          </span>
        </button>
        {open && (
          <div className="max-h-[50vh] overflow-auto absolute border-2 border-neutral-700">
            <ul className="list-none grid auto-rows-fr px-0  bg-white border-l-2 border-r-2 border-neutral-800 w-full">
              {labels.map((label, index) => {
                // we can't filter these out ahead of time because it screws up the indexing
                if (!label) {
                  return null;
                }
                return (
                  <li
                    key={label}
                    className="py-4 px-5 border-b border-warm-400 grid content-center"
                  >
                    <Anchor
                      onClick={() => {
                        setVisibleSection(index);
                        setOpen(false);
                      }}
                      link={`#${index}header`}
                      className="tra-text-web-body-xl hover:text-brand-400"
                    >
                      {label}
                    </Anchor>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavList;
